import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../account/accountStore.ts';
import {
  ALL_AUTH_METHODS,
  type AuthErrorCode,
  type AuthMethods,
  type Result,
} from '../../account/types.ts';
import { getAccountStore } from '../../account/accountStore.ts';
import styles from './Account.module.css';

/** Loads the account service when an account screen opens, and says where things stand. */
/** The ways of getting in that this build switches on (all of them when it does not say). */
export function useAuthMethods(): AuthMethods {
  return useAccount((s) => s.setup.methods) ?? ALL_AUTH_METHODS;
}

export function useAccountSession() {
  const available = useAccount((s) => s.setup.available);
  const mode = useAccount((s) => s.setup.mode);
  const load = useAccount((s) => s.load);
  const snapshot = useAccount((s) => s.snapshot);
  const client = useAccount((s) => s.client);
  useEffect(() => {
    if (available) void getAccountStore().getState().ensure();
  }, [available]);
  return { available, mode, load, snapshot, client };
}

/** One request at a time from a form: whether it is under way, and what went wrong with the last one. */
export function useAction() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<AuthErrorCode | null>(null);
  const run = useCallback(async <T,>(fn: () => Promise<Result<T>>): Promise<Result<T>> => {
    setPending(true);
    setError(null);
    try {
      const result = await fn();
      if (!result.ok) setError(result.error.code);
      return result;
    } finally {
      setPending(false);
    }
  }, []);
  const clear = useCallback(() => {
    setError(null);
  }, []);
  return { pending, error, run, clear };
}

/** The sentence for an error, in the language of the app. */
export function FormError({ code }: { code: AuthErrorCode | null }) {
  const { t } = useTranslation('account');
  if (!code) return null;
  return (
    <p className={styles['error']} role="alert">
      {t(`errors.${code}`)}
    </p>
  );
}

export function FormNote({ children }: { children: ReactNode }) {
  return (
    <p className={styles['okNote']} role="status">
      {children}
    </p>
  );
}

/** The column every account page lives in. */
export function Page({ children }: { children: ReactNode }) {
  return <div className={styles['page']}>{children}</div>;
}

export function useDateFormat() {
  const { i18n } = useTranslation();
  return {
    date: (value: string | number | Date): string =>
      new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value)),
    time: (value: string | number | Date): string =>
      new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' }).format(new Date(value)),
  };
}
